import React, {
	useState,
	useEffect,
	useRef,
	useMemo,
	useCallback,
} from "react";
import { Box, CircularProgress, Alert } from "@mui/material";
import { Gantt, Willow } from "@svar-ui/react-gantt";
import { useTranslation } from "react-i18next";
import { useFilters } from "components/Elements/Filters";

import { useGanttData } from "./hooks/useGanttData";
import { useGanttZoom } from "./hooks/useGanttScale";
import useGanttEventHandlers from "./hooks/useGanttEventHandlers";
import useGanttWheelZoom from "./hooks/useGanttWheelZoom";
import useGanttDragAutoScroll from "./hooks/useGanttDragAutoScroll";
import useGanttDateRange from "./hooks/useGanttDateRange";
import useGanttScrollAnchor from "./hooks/useGanttScrollAnchor";
import useGanttReschedule from "./hooks/useGanttReschedule";
import useGanttRowOrder from "./hooks/useGanttRowOrder";
import useGanttZoomPersistence from "./hooks/useGanttZoomPersistence";
import { useGanttViewport } from "./hooks/useGanttViewport";
import useGanttColumns from "./hooks/useGanttColumns";
import GanttToolbar from "./components/GanttToolbar";
import GanttTaskContent from "./components/GanttTaskContent";
import GanttDragDatePreview from "./components/GanttDragDatePreview";
import GanttRescheduleDialog from "./components/GanttRescheduleDialog";
import { calculateDateRange } from "./utils/dateUtils";
import ColourConstants from "helpers/colourConstants";
import "./styles/ganttTheme.css";

const ganttContainerStyles = {
	height: "calc(100vh - 300px)",
	minHeight: "400px",
	width: "100%",
	display: "flex",
	flexDirection: "column",
	border: `1px solid ${ColourConstants.tableBorder}`,
	borderRadius: 0,
	backgroundColor: "#fff",
};

const ganttChartStyles = {
	flex: 1,
	minHeight: 0,
	overflow: "hidden",
	position: "relative",
};

const loadingOverlayStyles = {
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	p: 2,
	backgroundColor: "rgba(255, 255, 255, 0.9)",
	borderTop: `1px solid ${ColourConstants.tableBorder}`,
	position: "sticky",
	bottom: 0,
};

const emptyStateStyles = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	textAlign: "center",
	color: "#666",
	fontSize: "0.875rem",
};

const ServicesGantt = () => {
	const { t } = useTranslation();
	const apiRef = useRef();
	const [zoomConfig, setZoomConfig] = useState(null);
	const [ganttApi, setGanttApi] = useState(null);

	// Get filter values from context
	const { buildUrlParams } = useFilters();

	// Use buildUrlParams to get properly formatted filter parameters
	const filters = useMemo(() => {
		const params = buildUrlParams();
		return {
			...params,
			chips: params.chips || [],
		};
	}, [buildUrlParams]);

	const {
		ganttData: tasks,
		taskTypes,
		services,
		loading,
		loadingMore,
		error,
		hasMorePages,
		loadMore,
		applyRescheduledService,
	} = useGanttData(filters);

	const { scrollContainerRef } = useGanttViewport({
		loadMore,
		loadingMore,
		hasMorePages,
		servicesLength: services.length,
	});

	// Double-click handler to open service in new tab
	const handleTaskSelect = useCallback(
		(taskId) => {
			const task = tasks.find((t) => t.id === taskId);
			// Only allow opening child tasks (not parent work order tasks)
			if (task && task.serviceData && task.parent) {
				const serviceId = task.serviceData.id;
				window.open(`/app/services/${serviceId}`, "_blank");
			}
		},
		[tasks],
	);

	// Zoom levels: 0=year, 1=month, 2=week, 3=day and hour.
	// The library owns the zoom state. Its "zoom-scale" action keeps whatever
	// is under the given offset pinned in place, so both the buttons and the
	// wheel go through it and the config below is never re-passed.
	const zoomBy = useCallback(
		(dir) => {
			const chart = scrollContainerRef.current?.querySelector(".wx-chart");
			if (!chart) return;

			apiRef.current?.exec("zoom-scale", {
				dir,
				ratio: 0.5,
				offset: chart.clientWidth / 2,
			});
		},
		[scrollContainerRef],
	);

	const handleZoomIn = () => zoomBy(1);
	const handleZoomOut = () => zoomBy(-1);

	// Start over when the filters change and a new set of services loads
	useEffect(() => {
		setZoomConfig(null);
	}, [filters]);

	const { start: rangeStart, end: rangeEnd } = useMemo(
		() => calculateDateRange(tasks),
		[tasks],
	);

	// Paging in more services usually leaves the range untouched; keep the same
	// Date instances in that case so the Gantt is not re-initialised
	const startTime = rangeStart ? rangeStart.getTime() : null;
	const endTime = rangeEnd ? rangeEnd.getTime() : null;
	const dataRange = useMemo(
		() => ({
			start: startTime === null ? null : new Date(startTime),
			end: endTime === null ? null : new Date(endTime),
		}),
		[startTime, endTime],
	);

	const dateRange = useGanttDateRange(dataRange, filters);

	useGanttScrollAnchor({
		ganttApi: ganttApi,
		containerRef: scrollContainerRef,
		rangeStart: dateRange.start,
	});

	// Scale the starting granularity to the data, not to the padding around it
	const calculatedZoomConfig = useGanttZoom(dataRange);

	// Freeze the scale once the dates it is derived from are known
	useEffect(() => {
		if (!zoomConfig && dateRange.start) {
			setZoomConfig(calculatedZoomConfig);
		}
	}, [zoomConfig, calculatedZoomConfig, dateRange.start]);

	useGanttEventHandlers({
		ganttApi: ganttApi,
		onTaskSelect: handleTaskSelect,
	});

	useGanttWheelZoom({
		containerRef: scrollContainerRef,
		ganttApi: ganttApi,
	});

	useGanttDragAutoScroll({
		containerRef: scrollContainerRef,
		ganttApi: ganttApi,
	});

	const reorderService = useGanttRowOrder({ ganttApi: ganttApi, tasks });

	const handleRescheduled = useCallback(
		(serviceId, scheduledDate) => {
			if (!scheduledDate) return;

			applyRescheduledService(serviceId, scheduledDate);
			reorderService(serviceId);
		},
		[applyRescheduledService, reorderService],
	);

	const {
		pending: pendingReschedule,
		confirm: confirmReschedule,
		cancel: cancelReschedule,
	} = useGanttReschedule({
		ganttApi: ganttApi,
		onRescheduled: handleRescheduled,
	});

	const { zoom, cellWidth } = useGanttZoomPersistence({
		ganttApi: ganttApi,
		zoomConfig,
		tasks,
	});

	// Get columns configuration
	const columns = useGanttColumns();

	// A stable callback: the library re-initialises whenever this prop changes
	const handleGanttInit = useCallback((api) => {
		apiRef.current = api;
		setGanttApi(api);
	}, []);

	if (loading && services.length === 0) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error && services.length === 0) {
		return (
			<Box sx={{ p: 2 }}>
				<Alert severity="error">{t("message.failedToLoadServicesData")}</Alert>
			</Box>
		);
	}

	return (
		<Box sx={ganttContainerStyles}>
			{/* Toolbar with status */}
			<GanttToolbar
				onZoomIn={handleZoomIn}
				onZoomOut={handleZoomOut}
				statusText={t("label.showingServicesCount", {
					count: services.length,
				})}
			/>

			<Box
				ref={scrollContainerRef}
				sx={ganttChartStyles}
				className="emi-gantt-theme hide-drag hide-progress"
			>
				{/* Mounted only once the scale is known: the Gantt keeps whatever
				    zoom it settles on at init, so it must not start without one */}
				{zoom && dateRange.start && (
					<Willow>
						<Gantt
							init={handleGanttInit}
							tasks={tasks}
							columns={columns}
							zoom={zoom}
							cellWidth={cellWidth}
							taskTemplate={GanttTaskContent}
							taskTypes={taskTypes}
							lengthUnit={"hour"}
							durationUnit={"minute"}
							start={dateRange.start}
							end={dateRange.end}
						/>
					</Willow>
				)}

				<GanttDragDatePreview
					ganttApi={ganttApi}
					containerRef={scrollContainerRef}
				/>

				<GanttRescheduleDialog
					pending={pendingReschedule}
					onConfirm={confirmReschedule}
					onCancel={cancelReschedule}
				/>

				{!loading && tasks.length === 0 && (
					<Box sx={emptyStateStyles}>
						{t("message.noServicesFoundForFilters")}
					</Box>
				)}

				{loadingMore && (
					<Box sx={loadingOverlayStyles}>
						<CircularProgress size={20} sx={{ mr: 1 }} />
						<Box sx={{ fontSize: "0.875rem", color: "#666" }}>
							{t("message.loadingMoreServices")}
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default ServicesGantt;
